/**
 * Курсы валют на устройстве.
 *
 * Справка, а не данные: цена камня остаётся той, которую назвал поставщик,
 * пересчёт нигде не сохраняется. Нужен он для одного вопроса — «а это сколько
 * в долларах?», который на выставке задают по десять раз в день.
 *
 * Курсы кладём на устройство: в павильоне сети обычно нет, и последний
 * известный курс полезнее прочерка. Но дата, на которую он взят, показывается
 * всегда — иначе справка превращается в вранье.
 */
import { setting, setSetting } from '../db';
import { apiFetch } from '../sync';

export interface Rates {
  base: string;
  rates: Record<string, number>;
  rateDate: string | null;
  fetchedAt: string;
  stale: boolean;
}

let memo: Rates | null = null;

/** Валюты, между которыми переключаются чаще всего на азиатских выставках. */
export const HANDY_CURRENCIES = ['USD', 'THB', 'CNY', 'HKD', 'EUR', 'ILS'];

export async function loadRates(): Promise<Rates | null> {
  if (memo) return memo;
  const cached = await setting<Rates | null>('fxRates', null);
  if (cached) memo = cached;

  // Свежесть проверяем раз в полдня: чаще незачем, курс камня не двигает.
  const age = cached ? Date.now() - new Date(cached.fetchedAt).getTime() : Infinity;
  if (age < 12 * 60 * 60 * 1000) return memo;

  try {
    const res = await apiFetch('/api/rates');
    if (res.ok) {
      const fresh = (await res.json()) as Rates;
      memo = fresh;
      await setSetting('fxRates', fresh);
    }
  } catch { /* нет сети — остаёмся с прежними, они лучше пустоты */ }
  return memo;
}

/**
 * Пересчёт через базовую валюту. Возвращает null, если курса нет: показать
 * «примерно ноль» хуже, чем не показать ничего.
 */
export function convert(value: number, from: string, to: string, r: Rates | null): number | null {
  if (from === to) return value;
  if (!r) return null;
  const f = from === r.base ? 1 : r.rates[from];
  const t = to === r.base ? 1 : r.rates[to];
  if (!f || !t) return null;
  return (value / f) * t;
}

/** «на 10 сентября» — короткая подпись под пересчётом. */
export function ratesAsOf(r: Rates | null): string {
  if (!r) return '';
  const d = r.rateDate ?? r.fetchedAt;
  return new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}
